import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { apiOrganizerGetDonationPageList } from 'src/api/organization';
import { LayoutWithHeader } from 'src/components/common/LayoutWithHeader';
import { UiButton } from 'src/components/UI/Button';
import { UiCleanCheckbox } from 'src/components/UI/checkbox/CleanCheckbox';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import {
  UICleanSearchInputWithButtonOnChangeType,
  UISearchInput,
  UISearchInputWithButtonType,
} from 'src/components/UI/inputs/search';
import { UiTable } from 'src/components/UI/table/UiTable';
import {
  UiTableColumnItemType,
  UiTableInnerFiltersType,
  UiTableRefProps,
} from 'src/components/UI/table/UiTable.helpers';
import { UiToast } from 'src/components/UI/toasts';
import {
  DonationPageListItemType,
  DonationPageListRequestType,
  DonationPageTableResponseType,
} from 'src/models/DonationPage/List';
import { Enums } from 'src/models/enums';
import { BaseTableFilterFiltersType } from 'src/models/Table';
import { commonCampaignsColumns } from 'src/pages/Campaigns/common';
import { MyCampaignsModalArchive } from 'src/pages/Campaigns/components/modals/_archive';
import { MyCampaignsModalCopy } from 'src/pages/Campaigns/components/modals/_copy';
import { DonationPageListActionsBtn } from 'src/pages/DonationPageList/_gridActions';
import { DonationPageListModalsType } from 'src/pages/DonationPageList/_settings';
import { DonationPageListExportButton } from 'src/pages/DonationPageList/ExportButton';
import { DonationPageListModalCantArchive } from 'src/pages/DonationPageList/modals/_cantArchive';
import { DonationPageListModalTurnOnOff } from 'src/pages/DonationPageList/modals/_turnOnOff';
import { dispatchOverlaySliceShow } from 'src/store/overlay/helpers';
import { currentOrganizationInfoSelector } from 'src/store/user/selectors';
import { commonFormatCurrencyWithType } from 'src/utils/currency';
import { useAnyRequest } from 'src/utils/hooks/useAnyRequest';
import { useOneRequest } from 'src/utils/hooks/useOneRequest';
import { usePageTitle } from 'src/utils/hooks/usePageTitle';
import { useTHelper } from 'src/utils/i18n';
import { numberOrDefaultNumber } from 'src/utils/numbers';
import { useStateForm } from 'src/utils/stateForm';
import { SortDirectionEnum } from 'src/utils/types';
import { getSearchParams, setSearchParam } from 'src/utils/url';
import { MyCampaignsModalDelete } from '../Campaigns/components/modals/_delete';

type FormValues = UISearchInputWithButtonType & {
  PageSize: number;
  Filters: BaseTableFilterFiltersType;
  SortByProperty: string;
  SortDirection: SortDirectionEnum;
  PageIndex: number;
  ShowArchivedCampaigns: boolean;
};

export const DonationPageList: React.FC = () => {
  const { t } = useTranslation();

  const { CommonName: orgCommonName } = useSelector(currentOrganizationInfoSelector);

  const tHelper = useTHelper('donationPageList');

  usePageTitle(tHelper('pageTitle'));

  const { id } = useParams<{ id?: string }>();

  const formProps = useStateForm<FormValues>({
    defaultValues: {
      PageSize: 10,
      ShowArchivedCampaigns: getSearchParams('ShowArchivedCampaigns') === 'true',
      Filters: [],
    },
  });

  const { getValue, setValue, onSubmit: handleSubmit } = formProps;

  const [modalData, setModalData] = useState<{
    item: DonationPageListItemType | null;
    modalName: DonationPageListModalsType | null;
  }>({
    item: null,
    modalName: null,
  });

  const hideModal = useCallback(() => {
    setModalData({
      item: null,
      modalName: null,
    });
  }, []);

  const {
    apiCaller,
    loading,
    response: model,
  } = useOneRequest<DonationPageTableResponseType>(apiOrganizerGetDonationPageList, {
    saveResponse: true,
  });

  const onSubmit = useCallback(
    (ignorePageIndex = true) => {
      if (id) {
        handleSubmit((data: FormValues) => {
          if (ignorePageIndex) {
            setValue('PageIndex', 0);
          }

          const model: DonationPageListRequestType = cloneDeep({
            ...data,
            PageIndex: ignorePageIndex ? 0 : data.PageIndex,
            RequestParams: { PermanentUrl: id },
          });

          apiCaller(model);

          setSearchParam<Partial<{ PageIndex: number; ShowArchivedCampaigns: boolean }>>({
            ShowArchivedCampaigns: data.ShowArchivedCampaigns || undefined,
            PageIndex: model.PageIndex + 1,
          });
        })();
      }
    },
    [apiCaller, handleSubmit, id, setValue]
  );

  const onlySubmit = useCallback(() => onSubmit(), [onSubmit]);

  const getData = useCallback(
    (data: UiTableInnerFiltersType) => {
      setValue('Filters', data.Filters);
      setValue('PageIndex', data.PageIndex);
      setValue('SortByProperty', data.SortByProperty);
      setValue('SortDirection', data.SortDirection);

      onSubmit(false);
    },
    [onSubmit, setValue]
  );

  const openOverlay = useCallback(
    (id: number) => () => {
      dispatchOverlaySliceShow({ id, type: 'campaign' });
    },
    []
  );

  const tableRef = useRef<UiTableRefProps>(null);
  const reloadTableData = useCallback(() => tableRef.current?.reloadData(), []);

  const columns = useMemo((): UiTableColumnItemType<DonationPageListItemType>[] => {
    const statusCodes = Enums.campaigns.StatusCode;

    return [
      commonCampaignsColumns.Name(tHelper('titles.title')),
      {
        title: tHelper('titles.raised'),
        sortPropertyName: 'Raised',
        filter: {
          type: 'numeric',
          propertyName: 'Raised',
        },
        className: 'text-right',
        renderValue: ({ Raised, CurrencyType }) => commonFormatCurrencyWithType(Raised, CurrencyType, false, 0),
      },
      commonCampaignsColumns.StartDate(tHelper('titles.startDate')),
      {
        title: tHelper('titles.status'),
        sortPropertyName: 'DonationPageDisplayingStatusCode',
        defaultSortDirection: SortDirectionEnum.ASC,
        className: 'status text-center',
        filter: {
          type: 'lookup',
          propertyName: 'DonationPageDisplayingStatusCode',
          lookup: [
            { name: t('common.campaignStatuses.draft'), value: statusCodes.Draft },
            { name: t('common.campaignStatuses.live'), value: statusCodes.Started },
            { name: t('common.campaignStatuses.off'), value: statusCodes.Off },
          ],
        },
        renderValue: 'DonationPageDisplayingStatus',
      },
    ];
  }, [t, tHelper]);

  const uniqueChangeAvailabilityId = 'donationPageListChangeAvailability';

  useAnyRequest(uniqueChangeAvailabilityId, {
    onSuccess: () => {
      reloadTableData();

      UiToast.commonSuccessSaved();
    },
  });

  const enabledCreating = !!model?.EnableCreateDonationPageButton;

  const actions = useCallback(
    (item: DonationPageListItemType) => (
      <div className="open-campaign-container d-flex">
        {!item.IsArchived && (
          <UiButton tag="a" onClick={openOverlay(item.ID)}>
            <i className="material-icons rotate down">settings</i>
          </UiButton>
        )}

        {(item.CanEditStartSellingDate || item.CanArchive || item.CanUnarchive || item.CanDelete) && (
          <DonationPageListActionsBtn item={item} simple setModalData={setModalData} />
        )}
      </div>
    ),
    [openOverlay]
  );

  const chooseModal = useCallback(() => {
    switch (modalData.modalName) {
      case 'copy':
        return <MyCampaignsModalCopy item={modalData.item} hideModal={hideModal} donationPageList />;
      case 'delete':
        return <MyCampaignsModalDelete item={modalData.item} hideModal={hideModal} onSubmit={onSubmit} donationPage />;
      case 'cantarchive':
        return <DonationPageListModalCantArchive hideModal={hideModal} />;
      case 'turnonoff':
        return (
          <DonationPageListModalTurnOnOff
            hideModal={hideModal}
            item={modalData.item}
            uniqueChangeAvailabilityId={uniqueChangeAvailabilityId}
          />
        );
      case 'archive':
      case 'unarchive':
        return (
          <MyCampaignsModalArchive
            item={modalData.item}
            hideModal={hideModal}
            onSubmit={onSubmit}
            archive={modalData.modalName === 'archive'}
          />
        );
      default:
        return null;
    }
  }, [hideModal, modalData.item, modalData.modalName, onSubmit]);

  const onSearchSubmit = useCallback(
    (searchInputObj: UICleanSearchInputWithButtonOnChangeType) => {
      const newFilters = formProps
        .getValue('Filters')
        .filter(({ PropertyName }) => PropertyName !== searchInputObj.PropertyName);

      if (searchInputObj.Value) {
        newFilters.push(searchInputObj);
      }

      formProps.setValue({ Filters: newFilters, PageIndex: 0 });

      onSubmit();
    },
    [formProps, onSubmit]
  );

  return (
    <div className="container pb-150">
      <LayoutWithHeader header={tHelper('header')} title={orgCommonName}>
        <div className="account-management-table">
          <div className="header-controls clearfix">
            <div className="filters-block col-xs-12 col-sm-4">
              <UISearchInput
                formProps={formProps}
                placeholder={tHelper('searchInputPlaceholder')}
                onChange={onSearchSubmit}
              />
            </div>
            <div className="buttons-block col-xs-12 col-sm-8">
              <div className="hover-container">
                <UiInnerNotSPALink
                  className={`btn btn-solid btn-primary${enabledCreating ? '' : ' disabled'}`}
                  href={enabledCreating ? `/CampaignsSetup/CreateDonationPage/${id}` : ''}
                >
                  {tHelper('createNewOneBtnText')}
                </UiInnerNotSPALink>
              </div>
            </div>
          </div>

          <div className="card hoverable">
            <div className="card-content">
              <UiTable
                data={{ response: model, loading, error: null }}
                getData={getData}
                columns={columns}
                dummyTable
                itemsPerPage={getValue('PageSize')}
                actions={actions}
                manageActions
                ref={tableRef}
                footer={
                  <div className="flexible-table-footer">
                    {numberOrDefaultNumber(model?.TotalRecordsCount) > 0 && (
                      <DonationPageListExportButton id={id as string} />
                    )}

                    <UiCleanCheckbox
                      formProps={formProps}
                      label={tHelper('showArchived')}
                      name="ShowArchivedCampaigns"
                      id="ShowArchivedCampaigns"
                      onChange={onlySubmit}
                    />
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </LayoutWithHeader>

      {chooseModal()}
    </div>
  );
};
